import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fieldTime } from "utils/fieldFormat";

// The distance the node reported on every wake, oldest first, in `unit`
// ("cm" as reported, or "mm"); a reading the radar could not confirm is a gap.
export function historySeries(samples = [], unit = "cm") {
  const scale = unit === "mm" ? 10 : 1;
  return [...samples]
    .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp))
    .map((s) => ({
      timestamp: Date.parse(s.timestamp),
      distance:
        s.radar_valid === false ||
        s.distance_cm === null ||
        s.distance_cm === undefined
          ? null
          : scale === 1
            ? s.distance_cm
            : Math.round(s.distance_cm * scale * 100) / 100,
    }));
}

// The distance the node reported on every wake, from its telemetry, with the
// readings it could not confirm as gaps, and the reports shown as a file.
// On Field cameras (with the diagnostics, in cm) and under Tools on Radar
// analysis (`unit` "mm", like the rest of that page).
export default function RadarHistory({ samples = [], unit = "cm" }) {
  const series = historySeries(samples, unit);
  const download = () => {
    const blob = new Blob([JSON.stringify(samples, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "field-telemetry.json";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <section className="field-panel" aria-labelledby="field-radar-history">
      <div className="field-panel-head">
        <div>
          <h2 id="field-radar-history">Radar history</h2>
          <p>
            Measured distance · latest {samples.length} reports · gaps preserve
            invalid readings
          </p>
        </div>
        <button
          className="field-button"
          disabled={!samples.length}
          onClick={download}
        >
          Export displayed reports
        </button>
      </div>
      <div className="field-panel-body">
        {series.length ? (
          <div className="field-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series}>
                <CartesianGrid stroke="#e8eff1" vertical={false} />
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  domain={["dataMin", "dataMax"]}
                  tickFormatter={(v) => fieldTime(v)}
                  tick={{ fontSize: 9 }}
                  minTickGap={75}
                />
                <YAxis tick={{ fontSize: 10 }} unit={` ${unit}`} width={60} />
                <Tooltip labelFormatter={(v) => `${fieldTime(v)} SGT`} />
                <Line
                  dataKey="distance"
                  stroke="#0e8a77"
                  strokeWidth={2}
                  isAnimationActive={false}
                  dot={false}
                  connectNulls={false}
                  name={`Distance (${unit})`}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="field-caption">
            No telemetry has been reported by this node yet.
          </p>
        )}
      </div>
    </section>
  );
}
